import { useState, useEffect } from "react";
import { Flex, Text, Separator, TextField, Button } from "@radix-ui/themes";
import { toast } from "sonner";
import Switch from "@/components/shared/inputs/switch/Switch";
import {
  useGetAggregatedAutoResponseConfig,
  useUpdateAggregatedAutoResponseConfig,
} from "./api/aggregatedAutoResponse.api";

export function AggregatedAutoResponse() {
  const { data: config, isLoading } = useGetAggregatedAutoResponseConfig();
  const updateConfig = useUpdateAggregatedAutoResponseConfig();

  const [enabled, setEnabled] = useState(false);
  const [windowMinutes, setWindowMinutes] = useState(5);

  useEffect(() => {
    if (config) {
      setEnabled(config.aggregatedResponseEnabled);
      setWindowMinutes(config.aggregatedWindowMinutes);
    }
  }, [config]);

  const handleToggle = async (checked: boolean) => {
    setEnabled(checked);
    try {
      await updateConfig.mutateAsync({ aggregatedResponseEnabled: checked });
      toast.success(`Aggregated auto-response ${checked ? "enabled" : "disabled"}`);
    } catch {
      setEnabled(!checked);
      toast.error("Failed to update setting. Please try again.");
    }
  };

  const handleSave = async () => {
    const minutes = Number(windowMinutes);
    if (isNaN(minutes) || minutes < 1 || minutes > 60) {
      toast.error("Window duration must be between 1 and 60 minutes.");
      return;
    }
    try {
      await updateConfig.mutateAsync({ aggregatedWindowMinutes: minutes });
      toast.success("Window duration saved successfully.");
    } catch {
      toast.error("Failed to save window duration. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <Flex justify="center" py="8">
        <Text size="2" color="gray">
          Loading settings...
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="6">
      <Flex direction="column" gap="2">
        <Text size="6" weight="bold">
          Aggregated Auto-Response
        </Text>
        <Text size="3" color="gray">
          When enabled, auto-responses are held and sent as a single aggregated reply after the
          configured window elapses.
        </Text>
      </Flex>

      <Separator size="4" />

      <Flex justify="between" align="center">
        <Flex direction="column" gap="1">
          <Text size="3" weight="medium">
            Enable aggregated auto-response
          </Text>
          <Text size="2" color="gray">
            Collect responses within the time window before sending.
          </Text>
        </Flex>
        <Switch
          value={enabled}
          onChange={handleToggle}
          disabled={updateConfig.isLoading}
        />
      </Flex>

      {enabled && (
        <Flex direction="column" gap="3">
          <Flex direction="column" gap="1">
            <Text size="3" weight="medium">
              Aggregation window (minutes)
            </Text>
            <Text size="2" color="gray">
              Responses received within this window will be combined into one reply (1–60 minutes).
            </Text>
          </Flex>
          <Flex align="center" gap="3">
            <TextField.Root
              type="number"
              value={String(windowMinutes)}
              onChange={(e) => setWindowMinutes(Number(e.target.value))}
              min="1"
              max="60"
              style={{ width: "100px" }}
            />
            <Button
              size="2"
              onClick={handleSave}
              disabled={updateConfig.isLoading}
            >
              Save
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
}
